import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  listAllowedSupplierEmails,
  addAllowedSupplierEmail,
  updateAllowedSupplierEmail,
  removeAllowedSupplierEmail,
} from '../services/supplierAccountService';
import ConfirmationModal from '../components/ConfirmationModal';
import SupplierAutocomplete from '../components/SupplierAutocomplete';
import { terminology } from '../config/terminology';
import {
  FaEnvelope, FaCheckCircle, FaClock, FaCalendarAlt, FaEnvelopeOpenText,
  FaPlus, FaTrashAlt, FaSearch, FaPencilAlt, FaBuilding, FaExclamationTriangle,
} from 'react-icons/fa';

// Helper function to format Firestore Timestamp
const formatTimestamp = (timestamp) => {
  if (!timestamp || !timestamp._seconds) return 'N/A';
  const date = new Date(timestamp._seconds * 1000);
  const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true };
  return date.toLocaleDateString('en-AU', options);
};

const STATUS_STYLES = {
  registered: 'bg-green-100 text-green-800',
  new: 'bg-yellow-100 text-yellow-800',
};

const STATUS_LABELS = {
  registered: 'Registered',
  new: 'New',
};

// Status is always colour + text, never colour alone.
const StatusPill = ({ status }) => (
  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-800'}`}>
    {STATUS_LABELS[status] || status || 'Unknown'}
  </span>
);

// Legacy entries (added before ticket 52) have no clientId and can never complete signup -
// flag that clearly rather than rendering a blank cell.
const SupplierCell = ({ clientId, clientName }) => {
  if (!clientId) {
    return (
      <span className="px-2 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">
        <FaExclamationTriangle className="mr-1" /> Not set
      </span>
    );
  }
  return <p className="text-gray-900 whitespace-nowrap">{clientName}</p>;
};

const PAGE_SIZE = 20;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TABLE_COLUMN_COUNT = 6; // Email, Supplier, Status, Date Added, Date Registered, Actions

// Elements a Tab loop should stop at, used to trap focus inside a modal.
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Locks body scroll, moves focus into the modal, traps Tab, closes on Escape, and returns
// focus to whatever opened the modal once it closes. Shared by the Add and Edit modals.
// `suspended` hands the keyboard to a dialog layered on top without tearing this trap down —
// unmounting it would unlock scroll and pull focus out from under that child dialog.
const useModalFocusTrap = (isOpen, { modalRef, triggerRef, onClose, suspended = false }) => {
  // The listener binds once per open, so both of these are read through a ref rather than
  // captured from the render that opened the modal.
  const suspendedRef = useRef(suspended);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    suspendedRef.current = suspended;
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = triggerRef?.current || document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Focus the first focusable control rather than a hardcoded field, since which field is
    // disabled (e.g. a locked email input) varies between the Add and Edit modals.
    modalRef.current?.querySelector(FOCUSABLE_SELECTOR)?.focus();

    const handleKeyDown = (e) => {
      if (suspendedRef.current) return; // a dialog on top of this one owns Escape and Tab
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = modalRef.current?.querySelectorAll(FOCUSABLE_SELECTOR);
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
};

// Skeleton rows shown while the list loads, instead of a spinner parked mid-page.
// Renders the real <thead> so headers don't appear only after loading finishes
// and shift the page.
const TableSkeleton = () => (
  <table className="min-w-full leading-normal">
    <thead>
      <tr className="bg-gray-100 text-left text-gray-600 uppercase text-sm">
        <th className="px-5 py-3 border-b-2 border-gray-200"><FaEnvelope className="inline mr-1" /> Email Address</th>
        <th className="px-5 py-3 border-b-2 border-gray-200"><FaBuilding className="inline mr-1" /> Supplier</th>
        <th className="px-5 py-3 border-b-2 border-gray-200"><FaCheckCircle className="inline mr-1" /> Status</th>
        <th className="px-5 py-3 border-b-2 border-gray-200"><FaCalendarAlt className="inline mr-1" /> Date Added</th>
        <th className="px-5 py-3 border-b-2 border-gray-200"><FaClock className="inline mr-1" /> Date Registered</th>
        <th className="px-5 py-3 border-b-2 border-gray-200 text-center">Actions</th>
      </tr>
    </thead>
    <tbody>
      {Array.from({ length: 6 }).map((_, row) => (
        <tr key={row} className="border-b border-gray-200">
          {Array.from({ length: TABLE_COLUMN_COUNT }).map((__, col) => (
            <td key={col} className="px-5 py-4">
              <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: col === TABLE_COLUMN_COUNT - 1 ? '2rem' : '80%' }} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

const ManageSupplierAccountsPage = () => {
  const [allowedEmails, setAllowedEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Search state
  const [search, setSearch] = useState('');
  const searchDebounceRef = useRef(null);

  // Pagination state — pageTokens[0] is always null (first page)
  const [pageTokens, setPageTokens] = useState([null]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);

  // State for Add Email Modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newEmailTouched, setNewEmailTouched] = useState(false);
  const [newSupplier, setNewSupplier] = useState(null); // { id, clientName } | null
  const [isAdding, setIsAdding] = useState(false);
  const addModalRef = useRef(null);
  const addModalTriggerRef = useRef(null);

  // State for Edit Email Modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [entryBeingEdited, setEntryBeingEdited] = useState(null); // the row's current data
  const [editEmail, setEditEmail] = useState('');
  const [editEmailTouched, setEditEmailTouched] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null); // { id, clientName } | null
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const editModalRef = useRef(null);
  const editModalTriggerRef = useRef(null);

  // Extra "this changes what a registered user can see" confirmation, only required when
  // reassigning the Supplier on an already-registered entry.
  const [isEditConfirmOpen, setIsEditConfirmOpen] = useState(false);
  const editConfirmRef = useRef(null);

  // State for Delete Confirmation Modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchEmails = useCallback(async (startAfterDocId, searchTerm) => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAllowedSupplierEmails({
        pageSize: PAGE_SIZE,
        startAfterDocId: startAfterDocId || null,
        search: searchTerm || '',
      });
      setAllowedEmails(data.emails || []);
      setHasNextPage(data.hasMore);
      if (data.hasMore && data.lastDocId) {
        setPageTokens((prev) => {
          const next = [...prev];
          next[currentPage + 1] = data.lastDocId;
          return next;
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to load supplier accounts.');
      setAllowedEmails([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Initial load and when page changes
  useEffect(() => {
    fetchEmails(pageTokens[currentPage], search);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Clear messages after a timeout
  useEffect(() => {
    let timer;
    if (successMessage || actionError) {
      timer = setTimeout(() => {
        setSuccessMessage(null);
        setActionError(null);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [successMessage, actionError]);

  useModalFocusTrap(isAddModalOpen, {
    modalRef: addModalRef,
    triggerRef: addModalTriggerRef,
    onClose: () => setIsAddModalOpen(false),
  });

  // Declared before the Edit modal's trap so that when a save closes both at once, this cleanup
  // runs first and the Edit modal's is the one that has the final say on scroll and focus.
  useModalFocusTrap(isEditConfirmOpen, {
    modalRef: editConfirmRef,
    onClose: () => { if (!isSavingEdit) setIsEditConfirmOpen(false); },
  });

  useModalFocusTrap(isEditModalOpen, {
    modalRef: editModalRef,
    triggerRef: editModalTriggerRef,
    onClose: () => handleCloseEditModal(),
    suspended: isEditConfirmOpen,
  });

  // --- Search ---
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(0);
      setPageTokens([null]);
      setHasNextPage(false);
      fetchEmails(null, val);
    }, 500);
  };

  const handleClearSearch = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setSearch('');
    setCurrentPage(0);
    setPageTokens([null]);
    setHasNextPage(false);
    fetchEmails(null, '');
  };

  // --- Pagination ---
  const handlePrevPage = () => {
    if (currentPage === 0) return;
    setCurrentPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (!hasNextPage) return;
    setCurrentPage((prev) => prev + 1);
  };

  // --- Add Email Handlers ---
  const handleOpenAddModal = (e) => {
    addModalTriggerRef.current = e?.currentTarget || null;
    setNewEmail('');
    setNewEmailTouched(false);
    setNewSupplier(null);
    setActionError(null);
    setIsAddModalOpen(true);
  };

  const emailIsValid = EMAIL_PATTERN.test(newEmail.trim());
  const canSubmitAdd = emailIsValid && !!newSupplier && !isAdding;

  const handleAddEmailSubmit = async (e) => {
    e.preventDefault();
    setNewEmailTouched(true);
    if (!emailIsValid) {
      setActionError('Enter a valid email address.');
      return;
    }
    if (!newSupplier) {
      setActionError(`Select a ${terminology.client.singular.toLowerCase()}.`);
      return;
    }
    setIsAdding(true);
    setActionError(null);
    setSuccessMessage(null);

    try {
      const response = await addAllowedSupplierEmail(newEmail.trim(), newSupplier.id);
      setCurrentPage(0);
      setPageTokens([null]);
      await fetchEmails(null, search);
      setSuccessMessage(response.message || `${newEmail} added to the supplier allowlist.`);
      setIsAddModalOpen(false);
      setNewEmail('');
      setNewSupplier(null);
    } catch (err) {
      setActionError(err.message || 'Failed to add email.');
    } finally {
      setIsAdding(false);
    }
  };

  // --- Edit Email Handlers ---
  const handleOpenEditModal = (entry, e) => {
    editModalTriggerRef.current = e?.currentTarget || null;
    setEntryBeingEdited(entry);
    setEditEmail(entry.email);
    setEditEmailTouched(false);
    setEditSupplier(entry.clientId ? { id: entry.clientId, clientName: entry.clientName } : null);
    setActionError(null);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    if (isSavingEdit) return;
    setIsEditModalOpen(false);
    setEntryBeingEdited(null);
  };

  const editEmailEditable = entryBeingEdited?.status === 'new';
  const editEmailIsValid = EMAIL_PATTERN.test(editEmail.trim());
  // Compared lower-cased because the backend stores the address as the doc id, lower-cased. A
  // case-only edit is not a change there, so treating it as one here would enable Save and then
  // take a 400 back.
  const editEmailNormalised = editEmail.trim().toLowerCase();
  const emailChanged = editEmailEditable && editEmailNormalised !== (entryBeingEdited?.email || '').toLowerCase();
  const supplierChanged = (editSupplier?.id || null) !== (entryBeingEdited?.clientId || null);
  // Supplier can be reassigned but not cleared back to unset once it was set - if the admin
  // clears the chip without picking a replacement, block save rather than send clientId: null.
  const supplierClearedWithoutReplacement = !editSupplier && !!entryBeingEdited?.clientId;
  const editHasChanges = emailChanged || supplierChanged;
  const canSubmitEdit = editHasChanges && !supplierClearedWithoutReplacement
    && (!editEmailEditable || editEmailIsValid) && !isSavingEdit;
  // Reassigning an already-registered entry's Supplier changes what that live supplier user
  // can see immediately, so it gets an extra danger confirmation before it's sent.
  const editRequiresConfirm = entryBeingEdited?.status === 'registered' && supplierChanged;

  const performEditSave = async () => {
    if (!entryBeingEdited) return;
    setIsSavingEdit(true);
    setActionError(null);
    setSuccessMessage(null);

    try {
      const payload = { email: entryBeingEdited.email };
      if (emailChanged) payload.newEmail = editEmailNormalised;
      if (supplierChanged) payload.clientId = editSupplier.id;
      const response = await updateAllowedSupplierEmail(payload);
      await fetchEmails(pageTokens[currentPage], search);
      setSuccessMessage(response.message || `${response.data?.email || entryBeingEdited.email} updated.`);
      // The allowlist entry moved but the backend found no registered portal account to repoint,
      // so that user is still reading the old Supplier - the admin has to escalate.
      if (response.data?.registeredUserUpdated === false) {
        setActionError(`No registered ${terminology.client.singular.toLowerCase()} portal account was found for ${response.data?.email || entryBeingEdited.email}, so their existing access was not changed. Contact support.`);
      }
      setIsEditConfirmOpen(false);
      setIsEditModalOpen(false);
      setEntryBeingEdited(null);
    } catch (err) {
      setActionError(err.message || 'Failed to update email.');
      setIsEditConfirmOpen(false); // surface the error against the Edit modal, not the confirm dialog
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleEditFormSubmit = (e) => {
    e.preventDefault();
    setEditEmailTouched(true);
    if (!canSubmitEdit) return;
    if (editRequiresConfirm) {
      setIsEditConfirmOpen(true);
      return;
    }
    performEditSave();
  };

  // --- Delete Email Handlers ---
  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry);
    setActionError(null);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!entryToDelete) return;

    setIsDeleting(true);
    setActionError(null);
    setSuccessMessage(null);

    try {
      const response = await removeAllowedSupplierEmail(entryToDelete.email);
      setAllowedEmails((prev) => prev.filter((e) => e.email !== entryToDelete.email));
      setSuccessMessage(response.message || `${entryToDelete.email} removed.`);
      setIsDeleteModalOpen(false);
      setEntryToDelete(null);
    } catch (err) {
      setActionError(err.message || 'Failed to remove email.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <h1 className="text-3xl font-bold text-jims-cyan-dark flex items-center">
          <FaEnvelopeOpenText className="mr-3" /> Manage Supplier Accounts
        </h1>
        <div className="flex items-center space-x-4">
          <button
            onClick={handleOpenAddModal}
            className="bg-jims-cyan hover:bg-jims-cyan-dark text-white font-bold py-2 px-4 rounded inline-flex items-center transition duration-300 focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:ring-offset-2"
          >
            <FaPlus className="mr-2" />
            Add Supplier Email
          </button>
          <Link
            to="/clients"
            className="text-jims-cyan-dark hover:text-jims-cyan underline focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:ring-offset-2 rounded"
          >
            Back to {terminology.client.plural}
          </Link>
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-4 max-w-2xl">
        Emails on this list can sign up for the {terminology.client.singular.toLowerCase()} portal.
        Assign each email to a {terminology.client.singular.toLowerCase()} below — signup binds
        them to that {terminology.client.singular.toLowerCase()} only, they don't choose their own.
      </p>

      {/* Search Input */}
      <div className="mb-4 relative max-w-md">
        <label htmlFor="supplier-email-search" className="sr-only">Search by email</label>
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          id="supplier-email-search"
          type="text"
          placeholder="Search by email..."
          value={search}
          onChange={handleSearchChange}
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:ring-offset-2 focus:border-jims-cyan text-sm"
        />
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded" role="status">
          {successMessage}
        </div>
      )}
      {/* Action Error Message */}
      {actionError && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded" role="alert">
          Error: {actionError}
        </div>
      )}

      {loading && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden overflow-x-auto">
          <TableSkeleton />
        </div>
      )}

      {error && !loading && (
        <div className="text-center text-red-700 bg-red-100 border border-red-300 p-4 rounded mb-4">
          <p className="mb-2">{error}</p>
          <button
            onClick={() => fetchEmails(pageTokens[currentPage], search)}
            className="underline font-medium focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 rounded"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            {allowedEmails.length === 0 ? (
              search ? (
                <p className="text-center text-gray-500 p-6">
                  No supplier emails match "{search}". Try a different search or{' '}
                  <button
                    onClick={handleClearSearch}
                    className="text-jims-cyan-dark underline focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:ring-offset-2 rounded"
                  >
                    clear the search
                  </button>.
                </p>
              ) : (
                <div className="text-center text-gray-500 p-8">
                  <p className="mb-3">No supplier emails have been added yet.</p>
                  <p className="text-sm mb-4">
                    Add an email to let a supplier sign up for the supplier portal.
                  </p>
                  <button
                    onClick={handleOpenAddModal}
                    className="bg-jims-cyan hover:bg-jims-cyan-dark text-white font-bold py-2 px-4 rounded inline-flex items-center transition duration-300 focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:ring-offset-2"
                  >
                    <FaPlus className="mr-2" /> Add Supplier Email
                  </button>
                </div>
              )
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full leading-normal">
                  <thead>
                    <tr className="bg-gray-100 text-left text-gray-600 uppercase text-sm">
                      <th className="px-5 py-3 border-b-2 border-gray-200"><FaEnvelope className="inline mr-1" /> Email Address</th>
                      <th className="px-5 py-3 border-b-2 border-gray-200"><FaBuilding className="inline mr-1" /> Supplier</th>
                      <th className="px-5 py-3 border-b-2 border-gray-200"><FaCheckCircle className="inline mr-1" /> Status</th>
                      <th className="px-5 py-3 border-b-2 border-gray-200"><FaCalendarAlt className="inline mr-1" /> Date Added</th>
                      <th className="px-5 py-3 border-b-2 border-gray-200"><FaClock className="inline mr-1" /> Date Registered</th>
                      <th className="px-5 py-3 border-b-2 border-gray-200 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allowedEmails.map((item) => (
                      <tr key={item.email} className="hover:bg-gray-50">
                        <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                          <p className="text-gray-900 whitespace-nowrap">{item.email}</p>
                        </td>
                        <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                          <SupplierCell clientId={item.clientId} clientName={item.clientName} />
                        </td>
                        <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                          <StatusPill status={item.status} />
                        </td>
                        <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                          <p className="text-gray-900 whitespace-nowrap">{formatTimestamp(item.addedAt)}</p>
                        </td>
                        <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                          <p className="text-gray-900 whitespace-nowrap">{formatTimestamp(item.registeredAt)}</p>
                        </td>
                        <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm text-center">
                          <div className="flex items-center justify-center gap-3">
                            <button
                              onClick={(e) => handleOpenEditModal(item, e)}
                              className="text-jims-cyan-dark hover:text-jims-cyan transition duration-300 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:ring-offset-2 rounded"
                              aria-label={`Edit email ${item.email}`}
                              disabled={isDeleting}
                            >
                              <FaPencilAlt />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(item)}
                              className="text-red-500 hover:text-red-700 transition duration-300 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 rounded"
                              aria-label={`Remove email ${item.email}`}
                              disabled={isDeleting}
                            >
                              <FaTrashAlt />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {allowedEmails.length > 0 && (
            <div className="flex items-center justify-between mt-4">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 0}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition duration-150 text-sm focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:ring-offset-2"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">Page {currentPage + 1}</span>
              <button
                onClick={handleNextPage}
                disabled={!hasNextPage}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition duration-150 text-sm focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:ring-offset-2"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Add Email Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center p-4">
          <div
            ref={addModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-supplier-email-title"
            className="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white"
          >
            <h3 id="add-supplier-email-title" className="text-lg font-medium leading-6 text-gray-900 mb-4">Add Supplier Email</h3>
            <form onSubmit={handleAddEmailSubmit} noValidate>
              <div className="mb-4">
                <label htmlFor="new-supplier" className="block text-sm font-medium text-gray-700">
                  {terminology.client.singular} *
                </label>
                <div className="mt-1">
                  <SupplierAutocomplete
                    id="new-supplier"
                    selectedSupplier={newSupplier}
                    onSelect={setNewSupplier}
                    onClear={() => setNewSupplier(null)}
                    placeholder={`Search ${terminology.client.plural.toLowerCase()}...`}
                    disabled={isAdding}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="new-email" className="block text-sm font-medium text-gray-700">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="new-email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onBlur={() => setNewEmailTouched(true)}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:ring-offset-2 focus:border-jims-cyan sm:text-sm ${newEmailTouched && !emailIsValid ? 'border-red-400' : 'border-gray-300'}`}
                  required
                  placeholder="supplier@example.com"
                  disabled={isAdding}
                  aria-invalid={newEmailTouched && !emailIsValid}
                  aria-describedby={newEmailTouched && !emailIsValid ? 'new-email-error' : undefined}
                />
                {newEmailTouched && !emailIsValid && (
                  <p id="new-email-error" className="mt-1 text-xs text-red-600">Enter a valid email address.</p>
                )}
              </div>

              {actionError && !successMessage && (
                <p className="text-sm text-red-600 mb-3">{actionError}</p>
              )}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition duration-150 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                  disabled={isAdding}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-jims-cyan text-white rounded hover:bg-jims-cyan-dark transition duration-150 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:ring-offset-2"
                  disabled={!canSubmitAdd}
                >
                  {isAdding ? 'Adding...' : 'Add Email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Email Modal */}
      {isEditModalOpen && entryBeingEdited && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center p-4">
          <div
            ref={editModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-supplier-email-title"
            className="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white"
          >
            <h3 id="edit-supplier-email-title" className="text-lg font-medium leading-6 text-gray-900 mb-4">Edit Supplier Email</h3>
            <form onSubmit={handleEditFormSubmit} noValidate>
              <div className="mb-4">
                <label htmlFor="edit-supplier" className="block text-sm font-medium text-gray-700">
                  {terminology.client.singular} *
                </label>
                <div className="mt-1">
                  <SupplierAutocomplete
                    id="edit-supplier"
                    selectedSupplier={editSupplier}
                    onSelect={setEditSupplier}
                    onClear={() => setEditSupplier(null)}
                    placeholder={`Search ${terminology.client.plural.toLowerCase()}...`}
                    disabled={isSavingEdit}
                  />
                </div>
                {supplierClearedWithoutReplacement && (
                  <p className="mt-1 text-xs text-red-600">Choose a replacement {terminology.client.singular.toLowerCase()}, or Cancel to leave it unchanged.</p>
                )}
              </div>

              <div className="mb-4">
                <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="edit-email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  onBlur={() => setEditEmailTouched(true)}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:ring-offset-2 focus:border-jims-cyan sm:text-sm disabled:bg-gray-100 disabled:text-gray-500 ${editEmailTouched && editEmailEditable && !editEmailIsValid ? 'border-red-400' : 'border-gray-300'}`}
                  required
                  disabled={!editEmailEditable || isSavingEdit}
                  aria-invalid={editEmailEditable && editEmailTouched && !editEmailIsValid}
                  aria-describedby={editEmailEditable && editEmailTouched && !editEmailIsValid ? 'edit-email-hint edit-email-error' : 'edit-email-hint'}
                />
                <p id="edit-email-hint" className="mt-1 text-xs text-gray-500">
                  {editEmailEditable
                    ? 'This entry hasn\'t registered yet, so the email can still be changed.'
                    : 'Already registered — this account is bound to this email address and it can\'t be changed here.'}
                </p>
                {editEmailEditable && editEmailTouched && !editEmailIsValid && (
                  <p id="edit-email-error" className="mt-1 text-xs text-red-600">Enter a valid email address.</p>
                )}
              </div>

              {actionError && !successMessage && (
                <p className="text-sm text-red-600 mb-3">{actionError}</p>
              )}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseEditModal}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition duration-150 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                  disabled={isSavingEdit}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-jims-cyan text-white rounded hover:bg-jims-cyan-dark transition duration-150 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:ring-offset-2"
                  disabled={!canSubmitEdit}
                >
                  {isSavingEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Extra confirmation: reassigning a registered entry's Supplier changes live portal access.
          Wrapped so its focus trap has a DOM boundary — ConfirmationModal takes no ref. */}
      <div ref={editConfirmRef}>
        <ConfirmationModal
          isOpen={isEditConfirmOpen}
          onClose={() => setIsEditConfirmOpen(false)}
          onConfirm={performEditSave}
          title={`Change ${terminology.client.singular}?`}
          message={entryBeingEdited && editSupplier
            ? `${entryBeingEdited.email} has already registered and is currently using ${entryBeingEdited.clientName || 'no ' + terminology.client.singular.toLowerCase()} in the ${terminology.client.singular.toLowerCase()} portal. Reassigning to ${editSupplier.clientName} changes what they can see immediately, effective right away. Continue?`
            : ''}
          isLoading={isSavingEdit}
          loadingText="Saving..."
          confirmText="Change Supplier"
          variant="danger"
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setEntryToDelete(null); }}
        onConfirm={handleConfirmDelete}
        title="Remove Supplier Email"
        message={entryToDelete
          ? `Remove ${entryToDelete.email} from the supplier allowlist? ${entryToDelete.status === 'registered' ? 'They have already registered, so this only stops a future signup with this email. It does not sign them out or revoke the access they already have. Contact support to disable their account.' : 'They will no longer be able to sign up with this email.'}`
          : ''}
        isLoading={isDeleting}
        loadingText="Removing..."
        confirmText="Remove"
        variant="danger"
      />
    </div>
  );
};

export default ManageSupplierAccountsPage;
